import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Avatar, AvatarGroup, IconButton, Tooltip } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import ReportAuthorizedBox from "@entrilia/shared/components/ReportAuthorizedBox.tsx";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import { checkAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import type { ReportAuthorizationInfo } from "../../../../../../api/types/svTypes.ts";
import type { SharedItemType } from "../../../sv/components/viewer/common/sharing/ShareReport.types.ts";
import SharedWithIcons from "./SharedWithIcons.tsx";

interface SharingCellItem {
  clientCode: string;
  inheritFromParent: boolean;
  authorization: ReportAuthorizationInfo;
}

interface Props<T extends SharingCellItem> {
  report: T;
  itemType?: SharedItemType;
  onShareClick: (report: T) => void;
}

export default function SharingCell<T extends SharingCellItem>({ report, itemType, onShareClick }: Props<T>) {
  const { hasAnyPermissionForClient } = useClientPermissionsContext();

  const handleShareClick = useCallback(() => {
    onShareClick(report);
  }, [onShareClick, report]);

  const canShare = useMemo(
    () =>
      hasAnyPermissionForClient(report.clientCode, ["ShareReports"]) &&
      checkAccess(report.authorization.access, ReportAccessType.FullAccess),
    [hasAnyPermissionForClient, report.clientCode, report.authorization.access]
  );

  const sharedWith = report.authorization.sharedWith ?? [];

  if (report.inheritFromParent) {
    const names = sharedWith.map((e) => e.name);
    const tooltip =
      names.length > 0 ? `Inherited from group\nShared with\n${names.join("\n")}` : "Inherited from group";
    return (
      <Tooltip
        title={tooltip}
        enterDelay={500}
        enterNextDelay={500}
        slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
        arrow
        disableInteractive
      >
        <AvatarGroup>
          <Avatar
            onClick={canShare ? handleShareClick : undefined}
            sx={{ width: 24, height: 24, cursor: canShare ? "pointer" : "default" }}
          >
            <FolderOutlinedIcon sx={{ width: 16, height: 16, color: "secondary.main" }} />
          </Avatar>
        </AvatarGroup>
      </Tooltip>
    );
  }

  if (report.authorization.private) {
    return (
      <ReportAuthorizedBox
        clientCode={report.clientCode}
        permissions={["ShareReports"]}
        requiredAccess={ReportAccessType.FullAccess}
        access={report.authorization.access}
      >
        <Tooltip title={`Share ${itemType ?? "report"}`} enterDelay={500} enterNextDelay={500} arrow disableInteractive>
          <IconButton className="sharing-action" onClick={handleShareClick} sx={{ cursor: "pointer" }}>
            <ShareOutlinedIcon />
          </IconButton>
        </Tooltip>
      </ReportAuthorizedBox>
    );
  }

  if (sharedWith.length === 0) {
    return null;
  }

  return (
    <SharedWithIcons
      sharedWith={sharedWith}
      tooltip={`Shared with\n${sharedWith.map((e) => e.name).join("\n")}`}
      onClick={canShare ? handleShareClick : undefined}
    />
  );
}
